/**
 * What kind of question was asked.
 *
 * The server runs the same three-way classification and returns its verdict on
 * the `done` frame; that verdict is authoritative and is what opens the thesis
 * door. This copy exists for the branches the frame cannot reach — a truncated
 * stream, and the composer deciding what to do before a byte comes back.
 *
 * The split is deliberately biased toward EVALUATIVE_MIXED. Wrong-toward-mixed
 * costs one redundant sentence; wrong-toward-pure costs the investor the
 * factual half of their question and shows them nothing that says so.
 */

export enum QuestionIntent {
  FACTUAL = 'FACTUAL',
  EVALUATIVE_MIXED = 'EVALUATIVE_MIXED',
  EVALUATIVE_PURE = 'EVALUATIVE_PURE',
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
}

/**
 * Other companies, other visitors, and the chat's own instructions. Checked
 * first: "how does this compare to other startups" is out of scope before it is
 * evaluative, because the comparison it wants is not on this record at all.
 */
const OUT_OF_SCOPE_PATTERNS = [
  /\b(other|another|similar|comparable|competing)\s+(compan|startup|founder|deal|profile)/,
  /\b(compan|startup)\w*\s+like\s+(this|it)\b/,
  /\bwho\s+else\s+(has\s+)?(asked|viewed|looked|visited|opened|read)/,
  // Only when it asks what they did here. "Which other investors are in the
  // round" is on the record and stays factual.
  /\b(other|another)\s+(investors?|visitors?|people|users?)\b[^?]*\b(ask|asked|say|said|think|thought|view|viewed|see|seen)\b/,
  /\banyone\s+else\s+(ask|asked|view|viewed|see|seen)/,
  /\b(system|your)\s+(prompt|instructions|rules)\b/,
  /\bignore\s+(all\s+|any\s+)?(previous|prior|the above)\b/,
  /\bwhat\s+model\s+(are\s+you|do\s+you\s+use)\b/,
  /\bwho\s+(made|built|trained)\s+you\b/,
]

/** Anything asking for a verdict, a rating or a recommendation. */
const EVALUATIVE_PATTERNS = [
  /\bgood\s+(investment|bet|deal|opportunity)\b/,
  /\b(should|would)\s+(i|we|you)\s+(invest|back|fund|pass)\b/,
  /\bworth\s+(investing|backing|a\s+meeting)\b/,
  /\b(rate|score|grade|rank)\s+(this|it|them|the\s+company|the\s+startup)\b/,
  /\bgive\s+(this|it|them)\s+a\s+(score|rating|grade)\b/,
  /\bhow\s+(good|strong|promising|risky|attractive|compelling)\b/,
  /\bhow\s+does\s+(this|it)\s+compare\b/,
  /\bbetter\s+than\b/,
  /\b(do|would)\s+you\s+recommend\b/,
  /\byour\s+(opinion|verdict|take|thoughts)\b/,
  /\bwhat\s+do\s+you\s+think\b/,
  /\bred\s+flags?\b/,
  /\bgood\s+(enough|for\s+an?)\b/,
  /\b(is|are)\s+(this|it|that|they|their|the|his|her)\s+[a-z ]{0,24}\b(good|strong|weak|solid|healthy|impressive|competitive|attractive|promising|risky|concerning)\b/,
]

/**
 * Something on the record the answer can be about. Its presence is what turns
 * a verdict question into a mixed one: there is a factual half worth answering.
 */
const FACTUAL_ANCHOR_PATTERNS = [
  /\b(traction|revenue|arr|mrr|growth|churn|retention|customers?|users?|pipeline)\b/,
  /\b(team|founders?|cto|ceo|hiring|headcount)\b/,
  /\b(market|tam|competitors?|moat|pricing)\b/,
  /\b(deck|slides?|record|profile|documents?|linkedin)\b/,
  /\b(round|raise|raising|valuation|safe|cheque|check|runway|burn|funds)\b/,
  /\b(seed|series|stage)\b/,
  /\b(product|technology|roadmap|milestones?)\b/,
  /\bhow\s+(much|many|long)\b/,
  /\b(when|where|which|who|numbers?|metrics?|dated?|sources?)\b/,
]

/**
 * A bare verdict question is short. Anything longer is carrying context, and
 * context is where the factual half hides — so length alone pushes to mixed.
 */
const MAX_BARE_QUESTION_WORDS = 12

export function classifyQuestionIntent(text: string): QuestionIntent {
  const question = text.toLowerCase().replace(/\s+/g, ' ').trim()
  if (question === '') return QuestionIntent.FACTUAL

  if (matches(OUT_OF_SCOPE_PATTERNS, question)) return QuestionIntent.OUT_OF_SCOPE
  if (!matches(EVALUATIVE_PATTERNS, question)) return QuestionIntent.FACTUAL

  const isBare =
    !matches(FACTUAL_ANCHOR_PATTERNS, question) &&
    question.split(' ').length <= MAX_BARE_QUESTION_WORDS
  return isBare ? QuestionIntent.EVALUATIVE_PURE : QuestionIntent.EVALUATIVE_MIXED
}

/** Whether the answer to this question should open the thesis door. */
export function isEvaluationQuestion(text: string): boolean {
  const intent = classifyQuestionIntent(text)
  return intent === QuestionIntent.EVALUATIVE_PURE || intent === QuestionIntent.EVALUATIVE_MIXED
}

function matches(patterns: readonly RegExp[], question: string): boolean {
  return patterns.some((pattern) => pattern.test(question))
}
